import BaseApiClient from '../BaseApiClient';
import {
  ListQuoteResponseInterface,
  StoreQuoteRequestInterface,
  QuoteItemsInterface,
  NoteInterface,
  QuoteInterface
} from '@interfaces/Quotes.interface';
import { AxiosPromise } from 'axios';
import { ListDataParams, ListDataResponse } from '@api-client/types';
import { InvoiceInterface } from '@interfaces/invoice.interface';

export class ApiQuote extends BaseApiClient {
  /**
   * Get quotes
   */
  async quotes(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<ListQuoteResponseInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.q = searchTerm;
      }

      this.client
        .getList('/simple-quotes', listData)
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta.current_page,
                perPage: res.data.meta.per_page,
                total: res.data.meta.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  get(id: number): Promise<QuoteInterface> {
    return new Promise((resolve) => {
      this.client
        .get(`/simple-quotes/${id}`)
        .then((res) => resolve(res.data))
        .catch(console.error);
    });
  }

  put(id: number, body: StoreQuoteRequestInterface): Promise<QuoteInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/simple-quotes/${id}`, body)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  store(
    body: StoreQuoteRequestInterface
  ): AxiosPromise<ListQuoteResponseInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/simple-quotes', body)
        .then((res) => resolve(res))
        .catch((res) => reject(res));
    });
  }

  deleteQuote(id: number): AxiosPromise<true> {
    return new Promise((resolve) => {
      this.client
        .delete(`/simple-quotes?quote_ids=${id}`)
        .then((res) => resolve(res))
        .catch(console.error);
    });
  }

  postStatus(id: number): AxiosPromise<ListQuoteResponseInterface> {
    return new Promise((resolve) => {
      this.client
        .post(`/simple-quotes/${id}/mark-as-sent`)
        .then((res) => resolve(res))
        .catch(console.error);
    });
  }

  postStatusApproved(id: number): AxiosPromise<ListQuoteResponseInterface> {
    return new Promise((resolve) => {
      this.client
        .post(`/simple-quotes/${id}/mark-as-approved`)
        .then((res) => resolve(res))
        .catch(console.error);
    });
  }

  recalculate(id: number): AxiosPromise<true> {
    return new Promise((resolve) => {
      this.client
        .post(`/simple-quotes/${id}/recalculate`)
        .then((res) => resolve(res))
        .catch(console.error);
    });
  }

  postConvertToInvoice(
    id: number,
    body: Partial<InvoiceInterface>
  ): Promise<ListQuoteResponseInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/simple-quotes/${id}/convert-to-invoice`, body)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  postSendToClient(
    quoteId: number,
    body: {
      to: string[];
      cc: string[];
      subject: string;
      message: string;
    }
  ) {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/simple-quotes/${quoteId}/send`, body)
        .then((res) => {
          if (res.status === 422) throw res;
          return resolve(res);
        })
        .catch((err) => reject(err));
    });
  }

  approveQuote(
    uuid: string,
    body: {
      approver_name: string;
      approver_email: string;
      approval_date: string;
    }
  ): AxiosPromise<ListQuoteResponseInterface> {
    return new Promise((resolve) => {
      this.client
        .post(`/simple-quotes/${uuid}/approve`, body)
        .then((res) => resolve(res))
        .catch(console.error);
    });
  }

  postAddItem(quoteId: number, body: Partial<QuoteItemsInterface>) {
    return new Promise((resolve) => {
      this.client
        .post(`simple-quotes/${quoteId}/items`, body)
        .then((res) => resolve(res.data))
        .catch(console.error);
    });
  }

  putItem(
    quoteId: number,
    id: number,
    body: Partial<QuoteItemsInterface>
  ): AxiosPromise<ListQuoteResponseInterface> {
    return new Promise((resolve) => {
      this.client
        .put(`/simple-quotes/${quoteId}/items/${id}`, body)
        .then((res) => resolve(res.data))
        .catch(console.error);
    });
  }

  deleteItems(quoteId: number, id: number): AxiosPromise<true> {
    return new Promise((resolve) => {
      this.client
        .delete(`/simple-quotes/${quoteId}/items?item_ids=${id}`)
        .then((res) => resolve(res))
        .catch(console.error);
    });
  }

  storeDocument(quoteId: number, files: Array<File>): Promise<QuoteInterface> {
    return new Promise((resolve) => {
      const fd = new FormData();

      files.map((file) => {
        return fd.append('files[]', file, file.name);
      });

      this.client
        .post(`simple-quotes/${quoteId}/documents`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .then((res) => resolve(res.data))
        .catch(console.error);
    });
  }

  deleteDocuments(quoteId: number, id: number[]): AxiosPromise<true> {
    return new Promise((resolve) => {
      const ids = id.reduce((prev, cur) => prev + cur + ',', '');
      this.client
        .delete(
          `/simple-quotes/${quoteId}/documents?document_ids=${ids.slice(
            0,
            ids.length - 1
          )}`
        )
        .then((res) => resolve(res))
        .catch(console.error);
    });
  }

  async noteslist(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<NoteInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.note_title = searchTerm;
      }
      this.client
        // TODO - Needs the api path once it's been created
        .getList('/note', listData)
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Store an item
   */

  async storeNote(dto: {
    entity_uuid: string;
    note_title: string;
    note_text: string;
  }): Promise<NoteInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/note', dto)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async deleteNote(id: number) {
    return this.client.delete(`/note/${id}`);
  }

  async exportPdf(category: string, quoteId: number): Promise<string> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/export-pdf/${category}/${quoteId}`, {
          responseType: 'blob',
          headers: { Accept: 'application/pdf' }
        })
        .then((res) => {
          if (res.data) {
            resolve(
              URL.createObjectURL(
                new Blob([res.data], { type: 'application/pdf' })
              )
            );
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
}

export { quoteQueryKeys } from './query-keys';
export type {
  QuoteNotesListQueryParams,
  QuotesListQueryParams
} from './query-keys';
export {
  useQuoteNotesListQuery,
  useQuoteQuery,
  useQuotesListQuery
} from './use-quote-queries';
export {
  useQuoteAddItemMutation,
  useQuoteApproveMutation,
  useQuoteConvertToInvoiceMutation,
  useQuoteDeleteDocumentsMutation,
  useQuoteDeleteItemsMutation,
  useQuoteDeleteMutation,
  useQuoteDeleteNoteMutation,
  useQuoteExportPdfMutation,
  useQuoteMarkAsApprovedMutation,
  useQuoteMarkAsSentMutation,
  useQuotePutItemMutation,
  useQuotePutMutation,
  useQuoteRecalculateMutation,
  useQuoteSendToClientMutation,
  useQuoteStoreDocumentMutation,
  useQuoteStoreMutation,
  useQuoteStoreNoteMutation
} from './use-quote-mutations';
export type {
  QuoteAddItemVariables,
  QuoteApproveVariables,
  QuoteConvertToInvoiceVariables,
  QuoteDeleteDocumentsVariables,
  QuoteDeleteItemsVariables,
  QuoteExportPdfVariables,
  QuotePutItemVariables,
  QuotePutVariables,
  QuoteSendToClientVariables,
  QuoteStoreDocumentVariables,
  QuoteStoreNoteVariables
} from './use-quote-mutations';
